/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Ref, useState } from "react";
import InputAdornment from "@material-ui/core/InputAdornment";
import { InputProps as CoreInputProps, TextField } from "@material-ui/core";
import noop from "lodash/fp/noop";
import { passwordFieldCss, VisibilityIcon, VisibilityOffIcon } from "./styles";
import { FlowioInputType } from "../../../../globalTypes";
import { textFieldInputCss } from "../commonStyles";
import { useBooleanState } from "../../../../hooks/utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InputComponent = ({ inputRef, ...props }: any) => (
  <input
    {...props}
    css={textFieldInputCss}
    ref={inputRef}
    data-cy="flowio-password-input"
    enterKeyHint="done"
  />
);

type ControlledOnlyProps = {
  value: string | null;
  defaultValue?: undefined;
  inputRef?: undefined;
};
type UncontrolledOnlyProps = {
  value?: undefined;
  defaultValue: string | null;
  inputRef?: Ref<any>;
};
type RefControlledOnlyProps = {
  value?: undefined;
  defaultValue?: undefined;
  inputRef: Ref<any>;
};

export type Props = (
  | ControlledOnlyProps
  | UncontrolledOnlyProps
  | RefControlledOnlyProps
) & {
  onChange?: (v: string) => void;
  onBlur?(): void;
  required?: boolean;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  onClick?: () => void;
  placeholder?: string;
  className?: string;
  name?: string;
  id?: string;
  InputProps?: CoreInputProps;
  disablePrefill?: boolean;
};

const FlowioPasswordInput: FC<Props> = ({
  value,
  defaultValue,
  required = false,
  disabled = false,
  onChange = noop,
  onBlur,
  inputRef,
  onClick,
  placeholder,
  className,
  name,
  id,
  InputProps,
  disablePrefill = false,
}) => {
  // https://gist.github.com/niksumeiko/360164708c3b326bd1c8#gistcomment-2032962
  const [beforeFirstFocus, setBeforeFirstFocus] = useState(true);
  const [showPassword, togglePassword] = useBooleanState(false);

  return (
    <TextField
      css={passwordFieldCss}
      type={showPassword ? FlowioInputType.Text : FlowioInputType.Password}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      InputProps={{
        readOnly: disablePrefill && beforeFirstFocus,
        defaultValue,
        disableUnderline: true,
        endAdornment: (
          <InputAdornment position="end" onClick={() => togglePassword()}>
            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </InputAdornment>
        ),
        inputComponent: InputComponent,
        inputRef,
        name,
        id,
        inputProps: {
          "data-cy": "input flowio-password-input",
        },
        onBlur,
        onFocus: () => setBeforeFirstFocus(false),
        onTouchStart: () => setBeforeFirstFocus(false),
        ...InputProps,
      }}
      className={className}
      onClick={onClick}
    />
  );
};

export default FlowioPasswordInput;
export type FlowioPasswordInputProps = Props;
